import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  ArrowLeft, Loader2, Wallet, AlertTriangle, ChevronDown, ChevronRight,
  Search, CheckCircle2, Clock, CalendarClock, Download, FileText,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_STYLE = {
  'Pending Timesheet Approval': 'bg-slate-100 text-slate-600',
  'Approved for Payment': 'bg-blue-100 text-blue-700',
  'Processing': 'bg-amber-100 text-amber-700',
  'Paid': 'bg-emerald-100 text-emerald-700',
  'Under Review': 'bg-purple-100 text-purple-700',
  'Overdue': 'bg-rose-100 text-rose-700',
};

const STATUS_OPTIONS = [
  { v: 'all', l: 'All statuses' },
  { v: 'pending_approval', l: 'Pending Timesheet Approval' },
  { v: 'approved', l: 'Approved for Payment' },
  { v: 'payment_processing', l: 'Processing' },
  { v: 'overdue', l: 'Overdue' },
  { v: 'paid', l: 'Paid' },
];

const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

export default function StaffPayments() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [payFor, setPayFor] = useState(null);
  const [reversingId, setReversingId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [fmt, setFmt] = useState('csv');
  const [acct, setAcct] = useState('');
  const [acctSaving, setAcctSaving] = useState(false);
  const [filters, setFilters] = useState({
    talent: '', client: '', date_from: '', date_to: '', status: 'all',
    settlement_from: '', settlement_to: '', outstanding_only: true,
  });

  const load = useCallback(async (f = filters) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      Object.entries(f).forEach(([k, v]) => {
        if (k === 'outstanding_only') qs.set(k, v);
        else if (v && v !== 'all') qs.set(k, v);
      });
      const r = await api.get(`/admin/settlements/outstanding?${qs.toString()}`);
      setData(r.data);
    } catch {
      toast.error('Could not load settlements');
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); /* initial */ // eslint-disable-next-line
  }, []);

  useEffect(() => {
    api.get('/admin/settlements/accounting-category')
      .then((r) => setAcct(r.data.value)).catch(() => {});
  }, []);

  const doExport = async (scope) => {
    setExporting(true);
    try {
      const qs = new URLSearchParams();
      if (scope === 'filtered') {
        Object.entries(filters).forEach(([k, v]) => {
          if (k === 'outstanding_only') qs.set(k, v);
          else if (v && v !== 'all') qs.set(k, v);
        });
      }
      qs.set('format', fmt);
      qs.set('scope', scope);
      const r = await api.get(`/admin/settlements/outstanding/export?${qs.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `outstanding_staff_payments_${scope}_${new Date().toISOString().slice(0, 10)}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export ready');
    } catch { toast.error('Export failed'); } finally { setExporting(false); }
  };

  const saveAcct = async () => {
    setAcctSaving(true);
    try {
      const r = await api.put('/admin/settlements/accounting-category', { value: acct });
      setAcct(r.data.value);
      toast.success('Accounting category saved');
    } catch { toast.error('Could not save category'); } finally { setAcctSaving(false); }
  };

  const reversePayment = async (it) => {
    if (!window.confirm(`Reverse the payment for ${it.talent_name || 'this talent'} — ${it.gig_name || 'shift'}? This marks it as NOT paid (outstanding) again so you can correct it. Use this only if it was marked paid by mistake.`)) return;
    setReversingId(it.booking_id);
    try {
      const reason = window.prompt('Optional note (e.g. "paid in Bluevine / wrong invoice"):', '') || '';
      const r = await api.post(`/admin/settlements/${it.booking_id}/reverse-external-payment`, { reason });
      toast.success(r.data.message || 'Payment reversed');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not reverse payment');
    } finally { setReversingId(null); }
  };

  const summary = data?.summary || {};
  const staff = data?.staff || [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="staff-payments-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <button onClick={() => navigate('/admin/home')} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-4" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Back to Admin
        </button>
        <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Wallet className="w-6 h-6 text-emerald-600" /> Outstanding Staff Payments
          </h1>
          <Button variant="outline" onClick={() => navigate('/admin/1099')} className="shrink-0" data-testid="view-1099-rollup-btn">
            <FileText className="w-4 h-4 mr-2" /> 1099 YTD Report
          </Button>
        </div>
        <p className="text-sm text-slate-500 mb-4">Who's owed, how much, for which event, and when their settlement is expected. Staff settlements are tracked separately from client invoices.</p>

        {/* Export toolbar */}
        <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="export-toolbar">
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
            <button className={`px-3 py-1.5 text-xs font-medium ${fmt === 'csv' ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600'}`} onClick={() => setFmt('csv')} data-testid="fmt-csv">CSV</button>
            <button className={`px-3 py-1.5 text-xs font-medium ${fmt === 'xlsx' ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600'}`} onClick={() => setFmt('xlsx')} data-testid="fmt-xlsx">Excel</button>
          </div>
          <Button variant="outline" size="sm" disabled={exporting} onClick={() => doExport('filtered')} data-testid="export-filtered-btn"><Download className="w-4 h-4 mr-1" /> Export Filtered</Button>
          <Button variant="outline" size="sm" disabled={exporting} onClick={() => doExport('all')} data-testid="export-all-btn"><Download className="w-4 h-4 mr-1" /> Export All Outstanding</Button>
          {exporting && <Loader2 className="w-4 h-4 animate-spin text-emerald-500" />}
          <div className="flex items-center gap-2 sm:ml-auto">
            <Label className="text-xs text-slate-500 whitespace-nowrap">Accounting category</Label>
            <Input className="h-8 w-44 text-xs" value={acct} onChange={(e) => setAcct(e.target.value)} data-testid="acct-category-input" title="Internal iEvntür accounting category applied to exported payments" />
            <Button variant="ghost" size="sm" disabled={acctSaving} onClick={saveAcct} data-testid="acct-category-save">Save</Button>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          <SummaryCard icon={Wallet} tone="emerald" label="Total outstanding" value={money(summary.total_outstanding)} sub={`${summary.staff_awaiting || 0} staff awaiting`} testid="sum-outstanding" />
          <SummaryCard icon={AlertTriangle} tone="rose" label="Overdue" value={money(summary.overdue_amount)} sub={`${summary.overdue_count || 0} settlements`} testid="sum-overdue" />
          <SummaryCard icon={CalendarClock} tone="blue" label="Scheduled (upcoming)" value={money(summary.upcoming_scheduled_amount)} sub={`${summary.upcoming_scheduled_count || 0} settlements`} testid="sum-scheduled" />
          <SummaryCard icon={Clock} tone="slate" label="Staff awaiting" value={summary.staff_awaiting || 0} sub="with a balance" testid="sum-staff" />
        </div>

        {/* Filters */}
        <Card className="mb-4"><CardContent className="p-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div><Label className="text-xs">Staff</Label><Input data-testid="f-talent" value={filters.talent} onChange={(e) => setFilters({ ...filters, talent: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && load()} placeholder="Name" /></div>
          <div><Label className="text-xs">Client</Label><Input data-testid="f-client" value={filters.client} onChange={(e) => setFilters({ ...filters, client: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && load()} placeholder="Company" /></div>
          <div><Label className="text-xs">Status</Label>
            <select data-testid="f-status" className="w-full h-10 rounded-md border border-slate-200 px-2 text-sm" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
              {STATUS_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </div>
          <div><Label className="text-xs">Event from</Label><Input data-testid="f-date-from" type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} /></div>
          <div><Label className="text-xs">Event to</Label><Input data-testid="f-date-to" type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} /></div>
          <div><Label className="text-xs">Settlement from</Label><Input data-testid="f-settle-from" type="date" value={filters.settlement_from} onChange={(e) => setFilters({ ...filters, settlement_from: e.target.value })} /></div>
          <div><Label className="text-xs">Settlement to</Label><Input data-testid="f-settle-to" type="date" value={filters.settlement_to} onChange={(e) => setFilters({ ...filters, settlement_to: e.target.value })} /></div>
          <div className="flex items-end justify-between gap-2">
            <label className="flex items-center gap-2 text-xs text-slate-600"><Switch checked={filters.outstanding_only} onCheckedChange={(v) => setFilters({ ...filters, outstanding_only: v })} data-testid="f-outstanding" /> Outstanding only</label>
            <Button onClick={() => load()} disabled={loading} data-testid="apply-btn"><Search className="w-4 h-4 mr-1" /> Apply</Button>
          </div>
        </CardContent></Card>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>
        ) : staff.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-slate-400 text-sm">No matching settlements.</CardContent></Card>
        ) : (
          <div className="space-y-2">
            {staff.map((g) => (
              <Card key={g.talent_id} data-testid={`staff-${g.talent_id}`}>
                <button className="w-full text-left p-4 flex items-center justify-between gap-3" onClick={() => setExpanded((e) => ({ ...e, [g.talent_id]: !e[g.talent_id] }))} data-testid={`staff-toggle-${g.talent_id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    {expanded[g.talent_id] ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                    <span className="font-semibold text-slate-800 truncate">{g.talent_name}</span>
                    {g.talent_is_test && <Badge className="bg-purple-100 text-purple-700 border-0 text-[10px]">TEST</Badge>}
                    {g.overdue_count > 0 && <Badge className="bg-rose-100 text-rose-700 border-0 text-[10px]">{g.overdue_count} overdue</Badge>}
                    <span className="text-xs text-slate-400">· {g.items.length} event{g.items.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-bold text-emerald-700">{money(g.total_outstanding)}</div>
                    <div className="text-[11px] text-slate-400">outstanding</div>
                  </div>
                </button>
                {expanded[g.talent_id] && (
                  <div className="border-t border-slate-100">
                    {g.items.map((it) => (
                      <div key={it.booking_id} className="px-4 py-3 border-b border-slate-50 last:border-0 flex items-center justify-between gap-3" data-testid={`item-${it.booking_id}`}>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-800 truncate">{it.gig_name}</div>
                          <div className="text-xs text-slate-500">
                            {fmtDate(it.date_worked)} · {it.client_name || '—'} · {it.hours_worked ?? '—'}h @ {money(it.contract_rate)}
                          </div>
                          <div className="text-[11px] text-slate-400 mt-0.5">
                            Approved {fmtDate(it.approved_date)} · Expected settlement <span className={it.is_overdue ? 'text-rose-600 font-medium' : ''}>{fmtDate(it.expected_settlement_date)}</span>
                            {it.settlement_terms_label ? ` · ${it.settlement_terms_label}` : ''}
                          </div>
                        </div>
                        <div className="text-right shrink-0 flex flex-col items-end gap-1">
                          {it.oa_deduction > 0 ? (
                            <div className="leading-tight" data-testid={`amt-${it.booking_id}`}>
                              <span className="text-[11px] text-slate-400 line-through mr-1">{money(it.gross_settlement)}</span>
                              <span className="font-semibold text-emerald-700">{money(it.net_settlement)}</span>
                              <div className="text-[10px] text-slate-400">net · − OA ins. {money(it.oa_deduction)}</div>
                            </div>
                          ) : (
                            <div className="font-semibold text-slate-800" data-testid={`amt-${it.booking_id}`}>{money(it.net_settlement)}</div>
                          )}
                          <Badge className={`border-0 text-[10px] ${STATUS_STYLE[it.status_label] || 'bg-gray-100 text-gray-600'}`}>{it.status_label}</Badge>
                          {it.status !== 'paid' && !it.already_settled && (
                            <button className="text-[11px] text-emerald-700 underline mt-0.5" onClick={() => setPayFor(it)} data-testid={`pay-btn-${it.booking_id}`}>Record payment</button>
                          )}
                          {(it.status === 'paid' || it.already_settled) && (
                            <button className="text-[11px] text-rose-600 underline mt-0.5 disabled:opacity-50" disabled={reversingId === it.booking_id} onClick={() => reversePayment(it)} data-testid={`reverse-btn-${it.booking_id}`}>
                              {reversingId === it.booking_id ? 'Reversing…' : 'Reverse payment'}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      {payFor && <RecordPaymentModal item={payFor} onClose={() => setPayFor(null)} onDone={() => { setPayFor(null); load(); }} />}
    </div>
  );
}

const SummaryCard = ({ icon: Icon, tone, label, value, sub, testid }) => {
  const tones = { emerald: 'text-emerald-600', rose: 'text-rose-600', blue: 'text-blue-600', slate: 'text-slate-600' };
  return (
    <Card data-testid={testid}><CardContent className="p-4">
      <div className={`flex items-center gap-1.5 text-xs font-medium ${tones[tone]}`}><Icon className="w-4 h-4" /> {label}</div>
      <div className="text-2xl font-bold text-slate-900 mt-1">{value}</div>
      <div className="text-[11px] text-slate-400">{sub}</div>
    </CardContent></Card>
  );
};

const RecordPaymentModal = ({ item, onClose, onDone }) => {
  const [form, setForm] = useState({ amount: item.net_settlement ?? '', method: 'zelle', reference: '', note: '', paid_on: '' });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/admin/settlements/${item.booking_id}/record-external-payment`, {
        amount: form.amount ? parseFloat(form.amount) : null,
        method: form.method, reference: form.reference.trim() || null,
        note: form.note.trim() || null, paid_on: form.paid_on || null,
      });
      if (res?.data?.already_paid) {
        toast.info(`${item.talent_name}'s payment was already recorded as paid.`);
      } else {
        toast.success(`Recorded ${money(parseFloat(form.amount || item.net_settlement))} to ${item.talent_name}`);
      }
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not record payment');
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="record-payment-modal">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600" /> Record external payment</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Record a payment made outside the platform for <b>{item.talent_name}</b> — {item.gig_name} ({fmtDate(item.date_worked)}). This marks the settlement Paid without creating a duplicate.</p>
        {item.oa_deduction > 0 && (
          <div className="text-xs text-slate-600 rounded bg-slate-50 border border-slate-200 px-2 py-1" data-testid="pay-oa-breakdown">
            Gross {money(item.gross_settlement)}{'  −  '}OA insurance {money(item.oa_deduction)} ({money(item.oa_rate_per_hour)}/hr){'  =  '}<b className="text-emerald-700">Net {money(item.net_settlement)}</b>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div><Label className="text-xs">Amount ($) — net payable</Label><Input data-testid="pay-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
          <div><Label className="text-xs">Method</Label>
            <select data-testid="pay-method" className="w-full h-10 rounded-md border border-slate-200 px-2 text-sm" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })}>
              {['zelle', 'check', 'cashapp', 'wire', 'venmo', 'manual'].map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div><Label className="text-xs">Reference #</Label><Input data-testid="pay-ref" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} /></div>
          <div><Label className="text-xs">Paid on</Label><Input data-testid="pay-date" type="date" value={form.paid_on} onChange={(e) => setForm({ ...form, paid_on: e.target.value })} /></div>
        </div>
        <div><Label className="text-xs">Note</Label><Input data-testid="pay-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="pay-cancel">Cancel</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={submit} data-testid="pay-confirm">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Mark paid</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
